import React, { useEffect, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { CircularProgress } from "@mui/joy";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  height: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

type ImageUploadModalProps = {
  open: boolean;
  handleClose: () => void;

  // ✅ 상위에서 관리하는 state setter
  setUploadFile: React.Dispatch<React.SetStateAction<File | null>>;
  setUploadFileName: React.Dispatch<React.SetStateAction<string>>;
  description?: string;
};

const ImageUploadModal2: React.FC<ImageUploadModalProps> = ({
  open,
  handleClose,
  setUploadFile,
  setUploadFileName,
    description,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);

  const setFileToParent = (file: File | null) => {
    if (!file) return;

    // 이미지 아닐 경우 무시
    if (!file.type.startsWith("image")) return;

    setUploadFile(file);
    setUploadFileName(file.name || "image.png");
    handleClose();
  };

  // 붙여넣기
    useEffect(() => {
        const handlePaste: EventListener = (evt) => {
            if (!open || loading) return;

            const e = evt as ClipboardEvent;

            const items = Array.from(e.clipboardData?.items ?? []);
            const imageItem = items.find((it) => it.type?.startsWith("image"));
            if (!imageItem) return;

            const file = imageItem.getAsFile();
            if (!file) return;

            const namedFile =
                file.name && file.name.length > 0
                    ? file
                    : new File([file], "pasted-image.png", { type: file.type });

            // 기존 함수 그대로 쓰면 됨
            setUploadFile(namedFile);
            setUploadFileName(namedFile.name);
            handleClose();
        };

        window.addEventListener("paste", handlePaste);
        return () => window.removeEventListener("paste", handlePaste);
    }, [open, loading, setUploadFile, setUploadFileName, handleClose]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileToParent(file);

    // 같은 파일 다시 선택 가능하게
    e.target.value = "";
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (loading) return;

    const file =
      e.dataTransfer.files?.[0] ??
      (e.dataTransfer.items?.[0]?.kind === "file"
        ? e.dataTransfer.items[0].getAsFile()
        : null);

    setIsDragOver(false);
    setFileToParent(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (loading) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 24,
              fontFamily: "Apple_R",
            }}
          >
            이미지 업로드
          </div>

          <div
            style={{
              flex: 0.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 12,
              fontFamily: "Apple_L",
              marginBottom : 8
            }}
          >
            (드래그 드롭, 복사 붙여넣기, 파일 선택)
          </div>

          <div
            style={{
              flex: 5,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "2px dashed #999",
              borderRadius: 12,
              background: isDragOver ? "rgba(0,0,0,0.05)" : "transparent",
              transition: "background 0.15s ease",
              cursor: loading ? "default" : "pointer",
              userSelect: "none",
            }}
            onClick={() => !loading && fileInputRef.current?.click()}
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {!loading ? (
              <div style={{ textAlign: "center" }}>
                <UploadIcon style={{ fontSize: 64 }} />
                <div style={{ marginTop: 8, fontSize: 12, color: "#333" }}>
                  1) 클릭 2) 드래그 드롭 3) 복사 붙여넣기
                </div>
              </div>
            ) : (
              <CircularProgress size="lg" />
            )}

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              disabled={loading}
            />
          </div>

          <div style={{ flex: 0.5 }} />

          <div
            style={{
              flex: 1,
              textAlign: "center",
              fontFamily: "Apple_R",
              fontSize: 14,
              whiteSpace : "pre-wrap"
            }}
          >
            이미지 파일을 업로드해주세요.
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default ImageUploadModal2;